import {
  type CareProgramEnrollment,
  useDischargeClientFromCareProgramMutation,
  useEnrollClientInCareProgramMutation,
} from '@cartwheel/api/v1'
import {
  Announcement,
  AnnouncementType,
  Badge,
  Button,
  Field,
  Select,
  Textarea,
  Tooltip,
} from '@cartwheel/components'
import { useState } from 'react'
import { extractApiErrorDetails } from '../utils/api-error-details'

interface CareProgramOption {
  id: string
  name: string
}

export interface CareProgramWidgetProps {
  clientId: string
  /** Serialized by CareProgramEnrollmentSerializer, so it shares the generated API type. */
  enrollment: CareProgramEnrollment | null
  carePrograms: CareProgramOption[]
  canEnroll: boolean
  canUnenroll: boolean
  schoolAvoidanceMessage: string | null
}

const CARD_CLASSES = 'mt-2 p-4 overflow-hidden bg-sand-25 shadow rounded-lg max-w-md'

// The Recent Activity feed is server-rendered in a lazy Turbo frame on the same page, so it
// only picks up the new timeline entry if we ask the frame to re-fetch itself.
function refreshRecentActivity() {
  const frame = document.querySelector<HTMLElement & { reload: () => void }>(
    'turbo-frame#client-events-index'
  )
  frame?.reload()
}

function enrolledTooltip(enrollment: CareProgramEnrollment): string {
  const enrolledOn = new Date(enrollment.enrolled_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return enrollment.enrolled_by_name
    ? `Enrolled ${enrolledOn} by ${enrollment.enrolled_by_name}`
    : `Enrolled ${enrolledOn}`
}

export default function CareProgramWidget({
  clientId,
  enrollment: initialEnrollment,
  carePrograms,
  canEnroll,
  canUnenroll,
  schoolAvoidanceMessage,
}: CareProgramWidgetProps) {
  const [enrollment, setEnrollment] = useState<CareProgramEnrollment | null>(initialEnrollment)
  const [panel, setPanel] = useState<'none' | 'enroll' | 'discharge'>('none')
  const [error, setError] = useState<string | null>(null)
  const [programId, setProgramId] = useState<string | null>(null)
  const [dischargeNote, setDischargeNote] = useState('')

  const enrollMutation = useEnrollClientInCareProgramMutation()
  const dischargeMutation = useDischargeClientFromCareProgramMutation()

  const closePanel = () => {
    setPanel('none')
    setError(null)
    setProgramId(null)
    setDischargeNote('')
  }

  const openPanel = (next: 'enroll' | 'discharge') => {
    setPanel(next)
    setError(null)
  }

  const handleEnroll = () => {
    if (!programId) return

    enrollMutation.mutate(
      {
        path: { client_id: clientId },
        body: { care_program_enrollment: { care_program_id: programId } },
      },
      {
        onSuccess: (data) => {
          setEnrollment(data)
          closePanel()
          refreshRecentActivity()
        },
        onError: (mutationError) => {
          setError(
            extractApiErrorDetails(mutationError) ??
              'Could not enroll this student. Please try again.'
          )
        },
      }
    )
  }

  const handleDischarge = () => {
    if (!enrollment) return

    dischargeMutation.mutate(
      {
        path: { client_id: clientId },
        body: {
          care_program_enrollment: {
            // So a stale page cannot close an episode the staff member never saw.
            care_program_id: enrollment.care_program.id,
            discharge_note: dischargeNote.trim() || null,
          },
        },
      },
      {
        onSuccess: () => {
          setEnrollment(null)
          closePanel()
          refreshRecentActivity()
        },
        onError: (mutationError) => {
          setError(
            extractApiErrorDetails(mutationError) ??
              'Could not unenroll this student. Please try again.'
          )
        },
      }
    )
  }

  const renderMembership = () => {
    if (enrollment) {
      return (
        <>
          <Tooltip text={enrolledTooltip(enrollment)}>
            <Badge
              label={enrollment.care_program.name}
              color="indigo"
              shape="pill"
              leftIcon="heart"
              wrapLabel={false}
            />
          </Tooltip>
          {canUnenroll && (
            <Button variant="link" onClick={() => openPanel('discharge')}>
              Unenroll
            </Button>
          )}
        </>
      )
    }

    if (schoolAvoidanceMessage) {
      return (
        <Tooltip text={schoolAvoidanceMessage}>
          <Badge
            label="School Avoidance Program"
            shape="pill"
            leftIcon="circle-info"
            wrapLabel={false}
          />
        </Tooltip>
      )
    }

    if (!canEnroll) return null

    return (
      <Badge
        label="Add care program"
        shape="pill"
        leftIcon="plus"
        wrapLabel={false}
        onClick={() => openPanel('enroll')}
      />
    )
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">{renderMembership()}</div>

      {panel === 'enroll' && (
        <div className={CARD_CLASSES}>
          {error && <Announcement type={AnnouncementType.Error} message={error} />}
          <Field.Root className="mt-2">
            <Field.Label>Care program</Field.Label>
            <Select<string>
              items={[
                { value: null, label: 'Select a program' },
                ...carePrograms.map((program) => ({ value: program.id, label: program.name })),
              ]}
              value={programId}
              onChange={setProgramId}
            />
          </Field.Root>
          <div className="flex gap-4 mt-4">
            <Button variant="secondary" onClick={closePanel}>
              Cancel
            </Button>
            <Button onClick={handleEnroll} disabled={!programId || enrollMutation.isPending}>
              Enroll
            </Button>
          </div>
        </div>
      )}

      {panel === 'discharge' && (
        <div className={CARD_CLASSES}>
          {error && <Announcement type={AnnouncementType.Error} message={error} />}
          <Field.Root className="mt-2">
            <Field.Label>Note (optional)</Field.Label>
            <Textarea
              rows={3}
              value={dischargeNote}
              onChange={(event) => setDischargeNote(event.target.value)}
            />
          </Field.Root>
          <div className="flex gap-4 mt-4">
            <Button variant="secondary" onClick={closePanel}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDischarge}
              disabled={dischargeMutation.isPending}
            >
              Confirm unenrollment
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
